import {
  useGetAnalysisEngineStatusQuery,
  type AnalysisEngineKind,
  type AnalysisEngineMessage,
  type AnalysisEngineState,
} from '@/shared/hooks/queries/generated';

// ============================================================================
// Statut du moteur d'analyses — source de la bannière.
// Contrat : docs/superpowers/api-changelog/2026-09-12-moteur-analyse-filter-service.md
// ============================================================================

/**
 * Gravité destinée à l'AFFICHAGE, dérivée de `state` SEUL depuis le 2026-09-16.
 *
 * ⚠️ `degraded` n'est PAS une erreur : une journée fraîchement indexée n'a pas
 * encore tous ses vecteurs, les analyses se calculent normalement. Le peindre en
 * rouge ferait passer un système sain pour un incident.
 * ⚠️⚠️ **`unavailable` EST TOUJOURS CRITIQUE.** Cette fonction lisait aussi
 * `sqlFallbackAvailable`, qui distinguait « repli SQL exact, l'utilisateur ne
 * perd qu'un peu de vitesse » de « analyses réellement indisponibles ». Le repli
 * a été SUPPRIMÉ (le magasin est le seul chemin), le champ avec lui : il ne
 * reste qu'un seul cas, et c'est le grave.
 */
export type AnalysisEngineSeverity = 'info' | 'warning' | 'critical';

export interface AnalysisEngineStatusView {
  severity: AnalysisEngineSeverity;
  /**
   * QUEL message afficher — le texte vient de `analysisEngine.messages.*` (i18n), jamais de l'API.
   * ⚠️ `vectorsInactive` s'interpole avec `incompleteVectorDays.length`.
   */
  messageCode: AnalysisEngineMessage;
  state: AnalysisEngineState;
  engine: AnalysisEngineKind;
  /** Jours que le magasin attend mais dont la tranche n'est pas à jour. */
  staleDays: string[];
  /** Jours dont la SIMILARITÉ seule peut être sous-évaluée (scan d'attributs intact). */
  incompleteVectorDays: string[];
}

/** Sondage doux : l'API met déjà son `/health` en cache ~10 s. */
const REFETCH_MS = 60_000;
const STALE_MS = 30_000;

const severityOf = (state: AnalysisEngineState): AnalysisEngineSeverity | null => {
  if (state === 'ok') return null;
  if (state === 'unavailable') return 'critical';
  // `degraded` — et toute valeur d'enum ajoutée plus tard côté API. Un état
  // qu'on ne connaît pas encore ne doit pas se peindre en rouge : c'est le
  // message de l'API qui parle, pas notre interprétation.
  return 'info';
};

/**
 * `status` vaut `null` quand il n'y a RIEN à dire : moteur sain, requête en vol,
 * ou requête en échec. Un état de moteur qu'on n'a pas su lire ne doit pas
 * fabriquer une bannière d'incident — ce serait annoncer une panne des analyses
 * à partir d'un simple hoquet réseau du front.
 */
export function useAnalysisEngineStatus(): { status: AnalysisEngineStatusView | null } {
  const { data } = useGetAnalysisEngineStatusQuery(undefined, {
    refetchInterval: REFETCH_MS,
    staleTime: STALE_MS,
    retry: 1,
  });

  const raw = data?.getAnalysisEngineStatus;
  if (!raw) return { status: null };

  const severity = severityOf(raw.state);
  // Pas de gravité, ou pas de code : rien à afficher. `messageCode` est `null`
  // quand `state === ok`, et c'est lui qui décide du texte rendu.
  if (!severity || !raw.messageCode) return { status: null };

  return {
    status: {
      severity,
      messageCode: raw.messageCode,
      state: raw.state,
      engine: raw.engine,
      staleDays: raw.staleDays,
      incompleteVectorDays: raw.incompleteVectorDays,
    },
  };
}
